import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Circle of Fifths Wheel v1 lexicon (Phase 3 Tier 3, tonal-geography tool).
 *
 * 12 entries — 3 tool/sub-mode + 5 relationships + 2 geometry + 2 feedback.
 * Copy is Bowie-locked verbatim from
 * `.claude/agent-memory/bowie/project_cof_wheel_v1_scope.md`. Do not
 * paraphrase; Bowie §8 reviews the round-trip for parallel-vs-relative
 * vocabulary discipline.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §8
 */
export const COF_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───── Tool + sub-modes (3) ─────
  {
    id: 'cof-wheel',
    category: 'circle',
    displayName: 'Circle of Fifths',
    displayNameShort: 'Circle',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Navigate music's family tree — see how keys are related and which ones sound nearby.",
      intermediate: 'Map key relationships visually — relative minor, closely-related keys, and key-signature siblings.',
      advanced:
        'The tonal-geography reference. Encodes key signatures, closely-related keys, and modulation routes via fifth/fourth motion.',
    },
    tagLabel: { beginner: 'key map', intermediate: 'circle of fifths', advanced: 'tonal geography' },
  },
  {
    id: 'cof-explore',
    category: 'circle',
    displayName: 'Wheel Explore',
    displayNameShort: 'Explore',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Click any key on the wheel to see what it sounds like and which keys are related to it.',
      intermediate: 'Click a key to audition it and see its relative minor, closely-related keys, and key signature.',
      advanced:
        'Free-explore the wheel. Click a tonic; the panel surfaces key signature + closely-related cluster; click to audition I–V.',
    },
    tagLabel: { beginner: 'click + hear', intermediate: 'explore keys', advanced: 'tonal navigation' },
  },
  {
    id: 'cof-quiz',
    category: 'circle',
    displayName: 'Key Relationship Quiz',
    displayNameShort: 'Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear a key and pick its musical neighbours — relative minor or closely-related keys.',
      intermediate: 'Identify relative-key + closely-related-key relationships on the wheel.',
      advanced: 'Quiz on relative + closely-related keys. Accepts any of the 5-key closely-related cluster.',
    },
    tagLabel: { beginner: 'find the neighbour', intermediate: 'key quiz', advanced: 'relationship quiz' },
  },
  // ───── Relationships (5) ─────
  {
    id: 'cof-relative-minor',
    category: 'circle',
    displayName: 'Relative minor',
    displayNameShort: 'Relative minor',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'The minor key that shares the same notes as a major key. C major and A minor are relative — same notes, different feel.',
      intermediate:
        'Relative minor — same key signature as the major key, but tonic is the 6th degree. C major ↔ A minor.',
      advanced:
        'Relative minor — vi of the major; shares the diatonic note collection. Aeolian-mode reading of the same key signature.',
    },
    tagLabel: { beginner: 'shared notes', intermediate: 'relative minor', advanced: 'vi / aeolian' },
  },
  {
    id: 'cof-relative-major',
    category: 'circle',
    displayName: 'Relative major',
    displayNameShort: 'Relative major',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The major key that shares the same notes as a minor key. A minor and C major are relative.',
      intermediate:
        'Relative major — same key signature as the minor key, but tonic is the ♭III degree (3 semitones up). A minor ↔ C major.',
      advanced:
        'Relative major — ♭III of the natural minor; shares the diatonic note collection. Ionian-mode reading of the same key signature.',
    },
    tagLabel: { beginner: 'shared notes', intermediate: 'relative major', advanced: '♭III / ionian' },
  },
  {
    id: 'cof-parallel',
    category: 'circle',
    displayName: 'Parallel key',
    displayNameShort: 'Parallel',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Same starting note, opposite feel — C major and C minor are parallel.',
      intermediate:
        'Parallel — same tonic, opposite mode (major ↔ minor). Different key signature; opposite side of the circle.',
      advanced:
        'Parallel — same tonic, opposite mode. Three sharps/flats apart on the circle. Used for borrowed chords (modal interchange).',
    },
    tagLabel: {
      beginner: 'same note, opposite mood',
      intermediate: 'parallel',
      advanced: 'modal interchange source',
    },
  },
  {
    id: 'cof-closely-related',
    category: 'circle',
    displayName: 'Closely-related key',
    displayNameShort: 'Closely related',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'Keys that share most of their notes — easy to move between. C major is closely related to F, G, Am, Dm, and Em.',
      intermediate:
        'Closely-related — key signature differs by ≤1 sharp/flat OR shares the same signature. Five keys per major.',
      advanced:
        'Closely-related cluster — IV / V / vi / ii / iii (each major has 5 closely-related keys; key signatures differ by ≤1).',
    },
    tagLabel: { beginner: 'easy modulation', intermediate: 'closely related', advanced: '±1 sharp/flat cluster' },
  },
  {
    id: 'cof-enharmonic',
    category: 'circle',
    displayName: 'Enharmonic keys',
    displayNameShort: 'Enharmonic',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'The same key spelled two ways — F♯ major and G♭ major sound identical.',
      intermediate: 'Enharmonic equivalents — F♯ (6 sharps) and G♭ (6 flats) name the same pitch collection differently.',
      advanced:
        "Enharmonic spelling at the 6-o'clock wrap. F♯/G♭, D♯m/E♭m. Choice of spelling typically follows surrounding harmony.",
    },
    tagLabel: { beginner: 'two spellings', intermediate: 'enharmonic', advanced: '6♯ ↔ 6♭ wrap' },
  },
  // ───── Geometry concepts (2) ─────
  {
    id: 'cof-key-signature',
    category: 'circle',
    displayName: 'Key signature',
    displayNameShort: 'Signature',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The sharps or flats at the start of a song — they tell you which key the song is in.',
      intermediate:
        'Key signature — the sharps or flats that apply throughout. Adjacent keys on the circle differ by one sharp/flat.',
      advanced:
        'Key signature — diatonic accidentals; each clockwise step around the circle adds one sharp (or removes one flat).',
    },
    tagLabel: { beginner: '♯s + ♭s', intermediate: 'key signature', advanced: 'diatonic accidentals' },
  },
  {
    id: 'cof-circle-geometry',
    category: 'circle',
    displayName: 'Circle geometry',
    displayNameShort: 'Geometry',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Each step around the wheel is up a 5th (clockwise) or down a 5th (counter-clockwise).',
      intermediate: 'Clockwise = +1 sharp (perfect fifth up). Counter-clockwise = +1 flat (perfect fifth down).',
      advanced:
        'Circle geometry — each clockwise step is a perfect fifth ascending and adds one sharp. Counter-clockwise reverses (perfect fourth ascending / fifth descending; +1 flat).',
    },
    tagLabel: { beginner: 'each step = 5th', intermediate: 'CW = +♯ / CCW = +♭', advanced: 'P5 cyclic group' },
  },
  // ───── Feedback wrappers (2) ─────
  {
    id: 'cof-explore-feedback',
    category: 'circle',
    displayName: 'Explore feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Try clicking neighbouring keys — they share most of their notes.',
      intermediate:
        'Adjacent keys on the circle are closely related. Across the circle = harmonically distant; parallel mode sits 3 positions away (same tonic, opposite quality).',
      advanced:
        'Position on the circle encodes harmonic distance — adjacent = closely-related; opposite = tritone distance (most remote); parallel mode lives 3 positions away (3-sharp/3-flat signature shift); relative minor sits on the inner ring at the same position.',
    },
    tagLabel: { beginner: 'try neighbours', intermediate: 'distance = relation', advanced: 'circular distance metric' },
  },
  {
    id: 'cof-quiz-feedback',
    category: 'circle',
    displayName: 'Quiz feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'The relative minor sits on the inner ring at the same position. Closely-related keys are the 5 keys around the question key.',
      intermediate:
        'Relative = inner-ring same position. Closely-related = the 5-key cluster (IV / V / vi / ii / iii) sharing ≤1 sharp/flat.',
      advanced:
        'Relative shares the diatonic collection; closely-related differ by ≤1 accidental. Memorize the cluster shape — it transposes around the wheel identically.',
    },
    tagLabel: { beginner: 'inner ring + neighbours', intermediate: '5-key cluster', advanced: 'cluster invariance' },
  },
  // ───── v1.5 Q-type intros (3) ─────
  // Copy finalized by Bowie §8 review (2026-05-29). Parallel-vs-relative
  // contrast is active in all three levels of cof-find-parallel per trap #1.
  // Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §8.1
  {
    id: 'cof-find-parallel',
    category: 'circle',
    displayName: 'Find the parallel',
    displayNameShort: 'Parallel',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'Find the key with the same starting note but the opposite feel. The parallel of C major is C minor — not A minor (that one is the relative).',
      intermediate:
        'Find the parallel: same tonic, opposite mode. C major → C minor. Different key signature — don’t confuse it with the relative, which keeps the signature and moves the tonic.',
      advanced:
        'Find the parallel — same tonic, opposite mode (e.g. C major ↔ C minor). The relative keeps the signature and changes tonic; the parallel keeps the tonic and changes the signature (by three accidentals for the natural-minor reading). Source of borrowed chords / modal interchange.',
    },
    tagLabel: {
      beginner: 'same note, opposite mood',
      intermediate: 'parallel ≠ relative',
      advanced: 'same tonic, opposite mode',
    },
  },
  {
    id: 'cof-find-signature',
    category: 'circle',
    displayName: 'Count the signature',
    displayNameShort: 'Signature count',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Count how many sharps or flats this key uses.',
      intermediate:
        'Count the accidentals in the key signature. Each step clockwise round the wheel adds a sharp; each step counter-clockwise adds a flat.',
      advanced:
        'Count diatonic accidentals. Clockwise = +1 sharp, counter-clockwise = +1 flat. At the 6-o’clock wrap a key has two valid counts (F♯ = 6♯, G♭ = 6♭).',
    },
    tagLabel: { beginner: 'count the ♯s', intermediate: 'count accidentals', advanced: 'signature reading' },
  },
  {
    id: 'cof-spell-enharmonic',
    category: 'circle',
    displayName: 'Spell the enharmonic',
    displayNameShort: 'Enharmonic spelling',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Some keys can be written two ways. Pick the spelling that uses the accidentals the prompt asks for.',
      intermediate:
        'Spell the key with the requested accidentals. F♯ major and G♭ major sound identical — pick the one that matches the prompt direction.',
      advanced:
        'Enharmonic respelling at the 6-o’clock wrap (B/C♭, F♯/G♭, C♯/D♭ and their relative minors). Same pitch collection, two names; choose the spelling whose accidentals match the asked direction.',
    },
    tagLabel: { beginner: 'two spellings', intermediate: 'enharmonic spelling', advanced: '6♯ ↔ 6♭ wrap' },
  },
  // ───── v1.5 sub-mode cards (2) ─────
  {
    id: 'cof-quiz-relationships',
    category: 'circle',
    displayName: 'Relationships quiz',
    displayNameShort: 'Relationships',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Practice spotting relative, parallel, and closely-related keys.',
      intermediate: 'Drill the three key relationships — relative, parallel, and the closely-related cluster.',
      advanced:
        'Relationship drill: relative (same signature, new tonic) vs parallel (same tonic, new signature) vs the closely-related cluster (signatures within one accidental).',
    },
    tagLabel: {
      beginner: 'find the neighbours',
      intermediate: 'relationship drill',
      advanced: 'tonal-distance drill',
    },
  },
  {
    id: 'cof-quiz-signatures',
    category: 'circle',
    displayName: 'Signatures quiz',
    displayNameShort: 'Signatures',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Practice counting sharps and flats, and spelling enharmonic keys.',
      intermediate: 'Drill key signatures — count the accidentals and pick the right enharmonic spelling.',
      advanced:
        'Signature drill: count accidentals (0–7) and choose the enharmonic spelling at the 6-o’clock wraps (B/C♭, F♯/G♭, C♯/D♭).',
    },
    tagLabel: {
      beginner: 'count ♯s and ♭s',
      intermediate: 'signature drill',
      advanced: 'accidental + enharmonic drill',
    },
  },
]
